// PRPathways.js - Portugal
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'Golden Visa (Investment)',
      subtitle: 'Residency through capital investment, real estate, or job creation',
      description: 'Portugal\'s most popular residency program for investors. Requires qualifying investment with minimal stay requirements (7 days/year).',
      details: {
        overview: 'The Golden Visa program grants residency permits to non-EU nationals who make qualifying investments in Portugal. Provides pathway to permanent residency and citizenship.',
        programs: [
          {
            name: 'Capital Transfer (€500,000)',
            requirements: 'Transfer minimum €500,000 to Portuguese bank account or investment in Portuguese companies. Funds must remain invested for 5 years. Provides flexibility in investment type.'
          },
          {
            name: 'Investment Funds (€500,000)',
            requirements: 'Investment of €500,000 in qualifying investment funds focused on Portuguese companies. Must maintain investment for minimum 5 years. Funds must be approved by Portuguese authorities.'
          },
          {
            name: 'Job Creation (10 jobs)',
            requirements: 'Create minimum 10 full-time jobs in Portugal for Portuguese nationals or legal residents. Jobs must be maintained for 5 years. Suitable for entrepreneurs and business owners.'
          },
          {
            name: 'Cultural Heritage Donation (€250,000)',
            requirements: 'Donation of €250,000 or more to support Portuguese cultural heritage, arts, or national heritage preservation. Non-refundable contribution.'
          },
          {
            name: 'Scientific Research (€500,000)',
            requirements: 'Investment of €500,000 in research activities conducted by Portuguese public or private scientific research institutions. Must be maintained for 5 years.'
          }
        ],
        keyPoints: [
          'Minimal stay requirement: 7 days per year (average over 5 years)',
          'Family reunification for spouse and dependent children',
          'Access to Schengen Area for travel',
          'Pathway to permanent residency after 5 years',
          'Eligible for citizenship after 5 years with basic Portuguese language',
          'Processing time: 6-12 months for initial application'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'D7 Visa (Passive Income)',
      subtitle: 'For retirees, remote workers, and individuals with passive income',
      description: 'Residency visa for those with stable passive income from pensions, rental income, investments, or remote work.',
      details: {
        overview: 'The D7 Visa is designed for non-EU nationals with sufficient passive income to support themselves in Portugal without needing local employment.',
        programs: [
          {
            name: 'Retirement/Pension Income',
            requirements: 'Proof of stable pension or retirement income. Minimum Portuguese minimum wage (€820/month for main applicant, 50% for spouse, 30% per child). Must show 12 months of income history and ability to continue.'
          },
          {
            name: 'Rental Income',
            requirements: 'Documented rental income from property ownership. Must demonstrate stable, recurring income meeting minimum thresholds. Property ownership documents and rental agreements required.'
          },
          {
            name: 'Investment Income',
            requirements: 'Income from dividends, interest, or other investments. Must show stable, passive income stream. Bank statements and investment portfolio documentation required.'
          },
          {
            name: 'Remote Work',
            requirements: 'Employment contract with non-Portuguese company allowing remote work. Salary must meet minimum income requirements. Employer letter confirming remote work arrangement required.'
          }
        ],
        keyPoints: [
          'Lower financial requirements than Golden Visa',
          'Must spend minimum 16 months in Portugal over 2-year period',
          'Renewable every 2 years, permanent residency after 5 years',
          'Access to Portuguese healthcare and education',
          'Eligible for NHR tax benefits (10 years)',
          'Processing time: 3-6 months'
        ]
      }
    },
    {
      icon: Users,
      title: 'Family Reunification & Other Visas',
      subtitle: 'D2 Entrepreneur, D3 Highly Qualified, Family Reunification',
      description: 'Additional residency pathways for entrepreneurs, highly skilled workers, and family members of Portuguese residents.',
      details: {
        overview: 'Portugal offers various residency visa categories beyond Golden Visa and D7, each with specific requirements and benefits.',
        programs: [
          {
            name: 'D2 Entrepreneur/Business Visa',
            requirements: 'Business plan for Portuguese company or self-employment. Minimum capital investment (typically €5,000-€10,000). Proof of business viability and economic benefit to Portugal. Must create employment or provide services.'
          },
          {
            name: 'D3 Highly Qualified Activity',
            requirements: 'Employment contract with Portuguese company for highly skilled position. Salary above Portuguese average (typically €1,500+/month). Relevant qualifications and experience. Employer must demonstrate need for foreign worker.'
          },
          {
            name: 'Family Reunification',
            requirements: 'Family member (spouse, children, parents) of Portuguese citizen or legal resident. Proof of relationship and family ties. Sponsor must have stable income and accommodation. No minimum investment required.'
          }
        ],
        keyPoints: [
          'D2 and D3 visas require active presence in Portugal',
          'Family reunification available for all visa types',
          'All pathways lead to permanent residency after 5 years',
          'Portuguese language requirement for citizenship (A2 level)',
          'Access to public services and EU travel',
          'Processing times vary: 2-6 months depending on visa type'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Available Residency Pathways
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Choose the pathway that best matches your profile, financial situation, and residency goals.
          </p>
        </motion.div>

        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#202356] mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-[#2542D4]" />
                    ) : (
                      <ChevronDown size={24} className="text-[#2542D4]" />
                    )}
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Investment Options & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-[#F5F8FA] p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-[#202356] mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Key Points
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-[#2542D4] rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
